import type { BillDataEntity } from "../../features/bills/domain/entities/BillEntity";
import type { LineItemEntity as BillLineItemEntity } from "../../features/bills/domain/entities/BillEntity";
import type { InvoiceDataEntity } from "../../features/invoices/domain/entities/InvoiceEntity";
import type { LineItemEntity as InvoiceLineItemEntity } from "../../features/invoices/domain/entities/InvoiceEntity";
import type { Currency } from "../../services/api";
import { getStateAbbreviation } from "../../utils";
import {
  getCompanyCurrencyCodeUpper,
  resolveDocumentCurrencyFields,
} from "./manualDocumentCurrency";

export type GstTypeValue = "IGST" | "CGST+SGST" | "NO_TAX";

export type BuildManualDocumentError = {
  success: false;
  errors: Record<string, string>;
};

export type BuildManualDocumentSuccess<T> = {
  success: true;
  data: T;
};

export type BuildManualDocumentResult<T> =
  | BuildManualDocumentSuccess<T>
  | BuildManualDocumentError;

export type ManualDocumentBuildOptions = {
  currencies: Currency[];
  companyGstEnabled: number;
  exchangeRateInput: string;
  companyCurrencyCode?: string;
  /**
   * Tally has no rate lookup, so a foreign-currency document must carry one.
   * Zoho pulls the latest rate itself, so pass false there to submit without it.
   */
  requireExchangeRate?: boolean;
};

type LineItemLike = {
  quantity?: number;
  rate?: number;
  item_total?: number;
  tax_percentage?: number;
  tax_amount?: number;
  tds_tax_id?: string | null;
  tds_tax_percentage?: number;
  item_order?: number;
  item_total_inclusive_of_tax?: number;
};

/** Zoho/external party id first; Zournal PK only as fallback. */
export function zournalPartyId(party: {
  vendorid?: string | number;
  id?: string | number;
  vendor_id?: string | number;
  zoho_vendorid?: string;
}): string {
  return String(
    party.zoho_vendorid ?? party.vendorid ?? party.id ?? party.vendor_id ?? "",
  ).trim();
}

/** Zoho/external customer id first; Zournal PK only as fallback. */
export function zournalCustomerId(party: {
  customerid?: string | number;
  id?: string | number;
  customer_id?: string | number;
  zoho_customerid?: string;
}): string {
  return String(
    party.zoho_customerid ??
      party.customerid ??
      party.id ??
      party.customer_id ??
      "",
  ).trim();
}

export function normalizeStateAbbrev(
  state: string | null | undefined,
): string {
  if (!state) return "";
  const normalized = state.toUpperCase().trim();
  if (normalized.length === 2) return normalized;
  try {
    return getStateAbbreviation(state) || normalized;
  } catch {
    return normalized;
  }
}

export function deriveGstFlags(
  kind: "bill" | "invoice",
  data: {
    source_of_supply?: string;
    destination_of_supply?: string;
    place_of_supply?: string;
  },
  isGstEnabled: boolean,
): { gst_type: GstTypeValue; is_igst: boolean } {
  if (!isGstEnabled) {
    return { gst_type: "NO_TAX", is_igst: false };
  }

  const sourceRaw =
    kind === "bill"
      ? data.source_of_supply ?? data.place_of_supply ?? ""
      : data.place_of_supply ?? data.source_of_supply ?? "";
  const destRaw = data.destination_of_supply ?? "";

  const source = normalizeStateAbbrev(sourceRaw);
  const dest = normalizeStateAbbrev(destRaw);

  if (!source || !dest) {
    return { gst_type: "CGST+SGST", is_igst: false };
  }

  const isInterState = source !== dest;
  return isInterState
    ? { gst_type: "IGST", is_igst: true }
    : { gst_type: "CGST+SGST", is_igst: false };
}

function roundMoney(value: number): number {
  return parseFloat((value || 0).toFixed(2));
}

function normalizeLineItems<T extends LineItemLike>(
  items: T[],
  isGstEnabled: boolean,
): T[] {
  return items.map((item, index) => {
    const quantity = Number(item.quantity ?? 0);
    const rate = Number(item.rate ?? 0);
    const itemTotal =
      item.item_total != null && item.item_total > 0
        ? Number(item.item_total)
        : roundMoney(quantity * rate);
    const taxPct = isGstEnabled ? Number(item.tax_percentage ?? 0) : 0;
    const taxAmount = isGstEnabled
      ? item.tax_amount != null
        ? Number(item.tax_amount)
        : roundMoney((itemTotal * taxPct) / 100)
      : 0;

    return {
      ...item,
      quantity,
      rate,
      item_total: itemTotal,
      tax_percentage: taxPct,
      tax_amount: taxAmount,
      item_order: index + 1,
      item_total_inclusive_of_tax: roundMoney(itemTotal + taxAmount),
    };
  });
}

function computeFinancialTotals<T extends LineItemLike>(
  lineItems: T[],
  opts: {
    isGstEnabled: boolean;
    discount: number;
    tcsTaxId?: string | null;
    tcsTaxPercentage?: number;
  },
): {
  sub_total: number;
  tax_total: number;
  tds_tax_amount: number;
  tcs_tax_amount: number;
  total: number;
  line_items: T[];
} {
  const normalized = normalizeLineItems(lineItems, opts.isGstEnabled);

  const sub_total = roundMoney(
    normalized.reduce((sum, item) => sum + Number(item.item_total ?? 0), 0),
  );
  const tax_total = opts.isGstEnabled
    ? roundMoney(
        normalized.reduce((sum, item) => sum + Number(item.tax_amount ?? 0), 0),
      )
    : 0;

  const tds_tax_amount = roundMoney(
    normalized.reduce((sum, item) => {
      const taxable = Number(item.item_total ?? 0);
      const pct = Number(item.tds_tax_percentage ?? 0);
      const hasTds =
        item.tds_tax_id &&
        String(item.tds_tax_id).trim() !== "" &&
        pct > 0;
      return sum + (hasTds ? (taxable * pct) / 100 : 0);
    }, 0),
  );

  const discount = roundMoney(opts.discount ?? 0);
  const baseBeforeTcs = roundMoney(sub_total + tax_total - discount);

  const tcsPct = Number(opts.tcsTaxPercentage ?? 0);
  const hasTcs =
    opts.tcsTaxId && String(opts.tcsTaxId).trim() !== "" && tcsPct > 0;
  const tcs_tax_amount = hasTcs
    ? roundMoney(baseBeforeTcs * (tcsPct / 100))
    : 0;

  const total = roundMoney(baseBeforeTcs + tcs_tax_amount);

  return {
    sub_total,
    tax_total,
    tds_tax_amount,
    tcs_tax_amount,
    total,
    line_items: normalized,
  };
}

function resolveCurrencyOrError(
  formState: {
    currency_id?: string;
    currency_code?: string;
    currency_symbol?: string;
    exchange_rate?: number | null;
  },
  options: ManualDocumentBuildOptions,
): ResolvedCurrencyResult {
  const resolved = resolveDocumentCurrencyFields(
    options.currencies,
    formState,
    options.exchangeRateInput,
    options.companyCurrencyCode ?? getCompanyCurrencyCodeUpper(),
    options.requireExchangeRate ?? true,
  );
  if (!resolved) {
    const docCode = String(formState.currency_code ?? "").trim().toUpperCase();
    const companyCode = (
      options.companyCurrencyCode ?? getCompanyCurrencyCodeUpper()
    ).trim();
    const needsFx =
      Boolean(companyCode) && Boolean(docCode) && companyCode !== docCode;
    return {
      ok: false,
      errors: needsFx
        ? {
            exchange_rate: "Exchange rate is required for foreign currency.",
            currency: "Could not resolve document currency.",
          }
        : { currency: "Could not resolve document currency." },
    };
  }
  return { ok: true, currency: resolved };
}

type ResolvedCurrencyResult =
  | {
      ok: true;
      currency: {
        currency_id: string;
        currency_code: string;
        currency_symbol: string;
        exchange_rate: number | null;
      };
    }
  | { ok: false; errors: Record<string, string> };

export function buildManualBillJson(
  formState: BillDataEntity,
  lineItems: BillLineItemEntity[],
  options: ManualDocumentBuildOptions,
): BuildManualDocumentResult<BillDataEntity> {
  const isGstEnabled = Number(options.companyGstEnabled) === 1;
  const currencyResult = resolveCurrencyOrError(formState, options);
  if (!currencyResult.ok) {
    return { success: false, errors: currencyResult.errors };
  }

  const gst = deriveGstFlags(
    "bill",
    {
      source_of_supply: formState.source_of_supply,
      destination_of_supply: formState.destination_of_supply,
      place_of_supply: formState.place_of_supply,
    },
    isGstEnabled,
  );

  const totals = computeFinancialTotals(lineItems, {
    isGstEnabled,
    discount: formState.discount ?? 0,
    tcsTaxId: formState.tcs_tax_id,
    tcsTaxPercentage: formState.tcs_tax_percentage,
  });

  return {
    success: true,
    data: {
      ...formState,
      ...currencyResult.currency,
      ...gst,
      ...totals,
      line_items: totals.line_items,
    },
  };
}

export function buildManualInvoiceJson(
  formState: InvoiceDataEntity,
  lineItems: InvoiceLineItemEntity[],
  options: ManualDocumentBuildOptions,
): BuildManualDocumentResult<InvoiceDataEntity> {
  const isGstEnabled = Number(options.companyGstEnabled) === 1;
  const currencyResult = resolveCurrencyOrError(formState, options);
  if (!currencyResult.ok) {
    return { success: false, errors: currencyResult.errors };
  }

  const sourceOfSupply =
    formState.source_of_supply?.trim() ||
    formState.place_of_supply?.trim() ||
    "";

  const gst = deriveGstFlags(
    "invoice",
    {
      source_of_supply: sourceOfSupply,
      destination_of_supply: formState.destination_of_supply,
      place_of_supply: formState.place_of_supply,
    },
    isGstEnabled,
  );

  const totals = computeFinancialTotals(lineItems, {
    isGstEnabled,
    discount: formState.discount ?? 0,
    tcsTaxId: formState.tcs_tax_id,
    tcsTaxPercentage: formState.tcs_tax_percentage,
  });

  return {
    success: true,
    data: {
      ...formState,
      source_of_supply: sourceOfSupply || formState.place_of_supply,
      ...currencyResult.currency,
      ...gst,
      ...totals,
      line_items: totals.line_items,
    },
  };
}
